import { apiClient, fetcher } from './apiClient';

/** A pending invitation, as shown on the Members page. */
type Invitation = {
    id: string;
    email: string;
    role: string;
    expiresAt: string;
    createdAt: string;
    invitedBy: string | null;
    /**
     * False when the invite was created but the mail server rejected it. The
     * row still exists and Resend will retry, so this is a warning rather than
     * a failure. Only present on create/resend responses.
     */
    emailSent?: boolean;
};

/** What the accept page renders before asking for a password. */
type InvitationPreview = {
    email: string;
    role: string;
    organization: string | null;
    expiresAt: string;
};

const jsonHeaders = (csrfToken: string | null) => ({
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': csrfToken ?? '',
});

const listInvitations = async (): Promise<Invitation[]> =>
    fetcher<Invitation[]>('api/organization/invitations');

const createInvitation = async (
    csrfToken: string | null,
    payload: { email: string; role: string },
): Promise<Invitation | undefined> => {
    if (csrfToken === null) return;
    const response = await apiClient.post(
        'api/organization/invitations',
        JSON.stringify(payload),
        { headers: jsonHeaders(csrfToken) },
    );

    return JSON.parse(response.data) as Invitation;
};

/** Revoking invalidates the emailed link immediately. */
const revokeInvitation = async (csrfToken: string | null, id: string): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.delete(`api/organization/invitations/${id}`, {
        headers: jsonHeaders(csrfToken),
    });
};

/** Issues a fresh token — the previously emailed link stops working. */
const resendInvitation = async (csrfToken: string | null, id: string): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.post(`api/organization/invitations/${id}/resend`, '', {
        headers: jsonHeaders(csrfToken),
    });
};

/**
 * Unauthenticated: read an invitation from its emailed token.
 *
 * Not routed through `fetcher` because callers need to distinguish a 410 (used,
 * revoked or expired) from a 404, and the shared 401 interceptor is irrelevant
 * here.
 */
const showInvitation = async (token: string): Promise<InvitationPreview> => {
    const response = await apiClient.get(`api/invitations/${encodeURIComponent(token)}`);

    return JSON.parse(response.data) as InvitationPreview;
};

const acceptInvitation = async (
    csrfToken: string | null,
    token: string,
    payload: { name: string; password: string; password_confirmation: string },
): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.post(
        `api/invitations/${encodeURIComponent(token)}/accept`,
        JSON.stringify(payload),
        { headers: jsonHeaders(csrfToken) },
    );
};

export {
    acceptInvitation,
    createInvitation,
    listInvitations,
    resendInvitation,
    revokeInvitation,
    showInvitation,
};
export type { Invitation, InvitationPreview };
